import { ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import {
  SidebarProvider,
  SidebarTrigger,
} from "@/project/karuna/components/ui/sidebar"; // @ts-ignore - sidebar is not a valid module
import { SchoolSidebar } from "@/project/karuna/components/school/SchoolSidebar"; // @ts-ignore - SchoolSidebar is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Bell, User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/project/karuna/components/ui/dropdown-menu"; // @ts-ignore - dropdown-menu is not a valid module
import {
  clearLocalStorage,
  getAccessToken,
  getUserInfo,
} from "@/lib/storeManager";
import {
  clearAllSchoolApprovalCaches,
  fetchSchoolApprovalForCurrentUser,
  normalizeSchoolMobile,
  readCachedSchoolApprovalForPhone,
} from "@/project/karuna/utils/schoolProfileApproval";

interface SchoolLayoutProps {
  children: ReactNode;
}

function getInitialLayoutApprovalState(): {
  approved: boolean;
  checking: boolean;
} {
  const phone = normalizeSchoolMobile(getUserInfo()?.phone_number ?? "");
  if (!phone) {
    return { approved: false, checking: true };
  }
  const cached = readCachedSchoolApprovalForPhone(phone);
  if (cached === true) {
    return { approved: true, checking: false };
  }
  if (cached === false) {
    return { approved: false, checking: false };
  }
  return { approved: false, checking: true };
}

export function SchoolLayout({ children }: SchoolLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const getLink = useKarunaLink();
  const initialLayout = getInitialLayoutApprovalState();
  const [isApproved, setIsApproved] = useState(initialLayout.approved);
  const [isCheckingApproval, setIsCheckingApproval] = useState(
    initialLayout.checking
  );

  // Check if user profile is completed
  const userInfo = getUserInfo();
  const isProfileCompleted = userInfo?.user_profile_completed ?? false;

  // Approval from full School Profile record (grid rows often omit status)
  const refreshApprovalState = useCallback(async () => {
    try {
      const { approved } = await fetchSchoolApprovalForCurrentUser();
      setIsApproved(approved);
    } catch (error) {
      console.error(
        "❌ Error checking approval status in SchoolLayout:",
        error
      );
      setIsApproved(false);
    } finally {
      setIsCheckingApproval(false);
    }
  }, []);

  useEffect(() => {
    void refreshApprovalState();
  }, [location.pathname, refreshApprovalState]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshApprovalState();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshApprovalState]);

  // Redirect to profile if:
  // 1. Not checking approval yet (wait for check to complete), OR
  // 2. Profile is not approved AND profile is not completed
  // If approved, allow access to all pages
  useEffect(() => {
    // Don't redirect while checking approval status
    if (isCheckingApproval) {
      return;
    }

    // If approved, allow access to all pages
    if (isApproved) {
      return;
    }

    // If not approved and not completed, redirect to profile
    if (!isProfileCompleted && !location.pathname.includes("/school/profile")) {
      navigate(getLink("/school/profile"));
    }
  }, [
    isApproved,
    isProfileCompleted,
    isCheckingApproval,
    location.pathname,
    navigate,
    getLink,
  ]);

  // Require school login - redirect to /school/login when not authenticated
  useEffect(() => {
    const checkAuth = () => {
      const accessToken = getAccessToken();
      const userInfo = getUserInfo();
      const hasSchoolAuth = accessToken && userInfo?.phone_number;
      if (!hasSchoolAuth) {
        navigate(getLink("/school/login"));
      }
    };
    checkAuth();
    const handleStorageChange = (e: StorageEvent) => {
      if (
        (e.key === "accessToken" && e.newValue === null) ||
        (e.key === "userInfo" && e.newValue === null)
      ) {
        navigate(getLink("/school/login"));
      }
    };
    const onFocus = () => {
      checkAuth();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        checkAuth();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = setInterval(checkAuth, 60_000);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [navigate, getLink]);

  const handleLogout = () => {
    clearAllSchoolApprovalCaches();
    clearLocalStorage();
    navigate(getLink("/school/auth"));
  };

  // Listen for contact number changes from admin (via BroadcastChannel)
  useEffect(() => {
    const normalizeMobileNumber = (mobileNumber: string): string => {
      return String(mobileNumber || "")
        .replace(/\D/g, "")
        .slice(0, 10);
    };

    const handleContactNumberChange = (event: MessageEvent) => {
      if (
        event.data?.type === "CONTACT_NUMBER_CHANGED" &&
        event.data?.oldContactNumber
      ) {
        const userInfo = getUserInfo();
        if (userInfo?.phone_number) {
          const loggedInMobileNumber = normalizeMobileNumber(
            userInfo.phone_number
          );
          const oldContactNumber = normalizeMobileNumber(
            event.data.oldContactNumber
          );

          // If the logged-in school user's mobile number matches the old contact number
          if (
            loggedInMobileNumber === oldContactNumber &&
            loggedInMobileNumber !== ""
          ) {
            console.log(
              "⚠️ Your contact number was changed by admin. Redirecting to auth..."
            );
            const authUrl = `${window.location.origin}${getLink("/school/auth")}`;
            // Redirect to auth page - don't clear localStorage
            // This preserves architecture-one login session
            window.location.href = authUrl;
          }
        }
      }
    };

    // Set up BroadcastChannel listener
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel("school-logout");
      channel.addEventListener("message", handleContactNumberChange);
    } catch (error) {
      console.warn("BroadcastChannel not supported:", error);
    }

    // Cleanup
    return () => {
      if (channel) {
        channel.removeEventListener("message", handleContactNumberChange);
        channel.close();
      }
    };
  }, [getLink]);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full overflow-x-hidden">
        <SchoolSidebar />

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-40 flex h-16 min-w-0 items-center justify-between border-b border-border bg-background px-3 sm:px-4 md:px-6">
            <SidebarTrigger />

            <div className="flex flex-shrink-0 items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 sm:h-10 sm:w-10"
              >
                <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-9 gap-1 sm:h-10 sm:gap-2"
                  >
                    <User className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden md:inline">School</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => navigate(getLink("/school/profile"))}
                  >
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout}>
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main className="min-w-0 flex-1 overflow-x-hidden bg-muted/10 p-3 sm:p-4 md:p-6">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
